/**
 * The v6 assessment block, shared by the two places a signal is read.
 *
 * A signal's detail is rendered twice — on the `/signals/:ref` permalink and in
 * Folder View's centre pane — from two near-duplicate interfaces. That was
 * already a mild redundancy; with ten more fields arriving in v6 it becomes the
 * kind where the two views quietly disagree about the same signal depending on
 * how you got there. So the fields and the markup live here, once.
 *
 * **Every field here can be null, and null means "not assessed".** None of them
 * is filled on any signal collected before v6, and nothing backfills them until
 * that pass runs. Printing a dash is therefore not a styling choice — printing
 * a default would be asserting an assessment of six thousand articles nobody
 * has looked at.
 */

/** The assessment fields, as `signals._out()` serialises them. */
export interface SignalAssessment {
  horizon: string | null;
  horizonRationale: string | null;
  sgAseanRelevance: number | null;
  watchArea: string | null;
  signalType: string | null;
  claimStatus: string | null;
  /** When the development happened, as against when it was reported. */
  eventDate: string | null;
  noveltyScore: number | null;
  story: { id: string; title: string } | null;
  origin: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  weak_signal: "Weak signal",
  emerging_issue: "Emerging issue",
  trend_evidence: "Trend evidence",
  event: "Event",
  commentary: "Commentary",
};

const CLAIM_LABELS: Record<string, string> = {
  occurred: "Occurred",
  claimed: "Claimed",
  alleged: "Alleged",
  disputed: "Disputed",
  proposed: "Proposed",
  planned: "Planned",
  study_finding: "Study finding",
  opinion: "Opinion",
};

const HORIZON_LABELS: Record<string, string> = {
  H1: "H1 · now to ~3 years",
  H2: "H2 · ~3–10 years",
  H3: "H3 · beyond 10 years, or plausibility unresolved",
};

const ORIGIN_LABELS: Record<string, string> = {
  collector: "Feed scan",
  manual_url: "Pasted URL",
  telegram_url: "Telegram link",
  telegram_pdf: "Telegram PDF",
  manual: "Typed in by hand",
};

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

/** A late disclosure is worth flagging: the guidance says briefs must mention
 *  one, and the gap is only visible if both dates are shown together. */
const LATE_DISCLOSURE_DAYS = 30;

function daysBetween(a: string, b: string): number | null {
  const from = Date.parse(a);
  const to = Date.parse(b);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86_400_000);
}

/**
 * The assessment as table rows, or `null` when the signal carries none of it.
 *
 * Returning null rather than a table of dashes is deliberate: on a corpus that
 * is entirely unclassified until the backfill runs, a section that is always
 * present and always empty teaches a reader to skip it — including on the day
 * it starts having something to say.
 */
export function renderAssessment(
  sig: SignalAssessment,
  datePublished?: string,
): string | null {
  const rows: string[] = [];

  const row = (label: string, value: string) =>
    rows.push(`<tr><td>${label}</td><td>${value}</td></tr>`);

  if (sig.signalType) {
    row("Type", esc(TYPE_LABELS[sig.signalType] ?? sig.signalType));
  }
  if (sig.claimStatus) {
    row("Claim status", esc(CLAIM_LABELS[sig.claimStatus] ?? sig.claimStatus));
  }
  if (sig.horizon) {
    const rationale = sig.horizonRationale
      ? `<br /><span class="muted">${esc(sig.horizonRationale)}</span>`
      : "";
    row("Horizon", `${esc(HORIZON_LABELS[sig.horizon] ?? sig.horizon)}${rationale}`);
  } else if (sig.horizonRationale) {
    // A rationale without a horizon means the model gave a reason and then an
    // unrecognised label, which is coerced to null on the way in. Showing the
    // reason anyway is more honest than dropping it.
    row("Horizon", `<span class="muted">unrecognised · ${esc(sig.horizonRationale)}</span>`);
  }
  if (sig.eventDate) {
    const gap = datePublished ? daysBetween(sig.eventDate, datePublished) : null;
    const late =
      gap !== null && gap > LATE_DISCLOSURE_DAYS
        ? ` <span class="sg-late">reported ${gap} days later</span>`
        : "";
    row("Happened", `${esc(sig.eventDate)}${late}`);
  }
  if (sig.story) {
    row(
      "Story",
      `<a href="/stories/${encodeURIComponent(sig.story.id)}" data-route>${esc(sig.story.title)}</a>`,
    );
  }
  if (sig.noveltyScore !== null && sig.noveltyScore !== undefined) {
    row("Novelty", `${sig.noveltyScore.toFixed(2)} <span class="muted">of 1.00</span>`);
  }
  if (sig.sgAseanRelevance !== null && sig.sgAseanRelevance !== undefined) {
    row("Singapore / ASEAN", `${sig.sgAseanRelevance}/10`);
  }
  if (sig.watchArea) {
    row("Watch area", esc(sig.watchArea));
  }
  if (sig.origin) {
    row("Came from", esc(ORIGIN_LABELS[sig.origin] ?? sig.origin));
  }

  if (!rows.length) return null;
  return `<div class="ap-table-wrap"><table class="ap-table sg-assessment">
    <tbody>${rows.join("")}</tbody>
  </table></div>`;
}
